import React from 'react'
import dayjs from 'dayjs'

import Vertical from '../layouts/Vertical.jsx'
import BarStackedHOC from '../charts/BarStackedHOC.jsx'
import PieChartHOC from '../charts/PieChartHOC.jsx'
import Card from '../components/Card.jsx'
import Grid from '../components/Grid.jsx'
import ZeroState from '../components/ZeroState.jsx'
import LoadingState from '../components/LoadingState.jsx'

import useRequest from '../hooks/useRequest.js'
import { getEntityDistribution, getEntitySentiment, RANGE_TYPES } from '_services/insights-v2.js'
import { largestRemainderRound } from '../../charts/helpers/formatters.js'

const TWO_DAYS_AGO = dayjs().subtract(2, 'day').toISOString().split('T').at(0)
const ONE_MONTH_AGO = dayjs().subtract(1, 'month').subtract(2, 'day').toISOString().split('T').at(0)
const LAST_MONTH_TIME_RANGE = {
  type: RANGE_TYPES.CUSTOM,
  beginning: ONE_MONTH_AGO,
  end: TWO_DAYS_AGO,
}

export const useFetchDistribution = (audienceId, entities) =>
  useRequest(
    getEntityDistribution,
    { audienceId, entities, timeRange: LAST_MONTH_TIME_RANGE },
    !!entities?.length
  )

export const useFetchSentiment = (audienceId, entities) =>
  useRequest(
    getEntitySentiment,
    { audienceId, entities, timeRange: LAST_MONTH_TIME_RANGE },
    !!entities?.length
  )

export const formatEntityComparison = (data, names) => {
  if (!data) return []

  const base = names.map((name) => {
    const key = name.toLowerCase().replaceAll(' ', '_')
    const value = data[key]?.percentage
    return {
      key: name,
      value: Number(value),
    }
  })

  const isAllEmpty = Object.values(data).filter(({ count }) => count !== 0).length === 0
  if (isAllEmpty) return base

  const roundedValues = largestRemainderRound(base.map(({ value }) => value))
  return base.map((item, index) => ({ ...item, value: roundedValues[index] }))
}

export const formatSentiment = (data, names) => {
  if (!data) return []
  return names.map((name) => {
    const key = name.toLowerCase().replaceAll(' ', '_')
    const sent = data[key]
    return {
      labels: name,
      negative: Number(sent?.negative),
      neutral: Number(sent?.neutral),
      positive: Number(sent?.positive),
    }
  })
}

const CompetitiveLandscape = (props) => {
  const {
    audience: { _id: audienceId },
  } = props
  const { title, description, section_1, section_2, selected_entities } = props.data

  const entities = selected_entities.map((entity) => {
    if (typeof entity === 'string') return entity
    return entity.orig_name
  })

  const { data: distributionData, loading: distributionLoading } = useFetchDistribution(
    audienceId,
    entities
  )
  const pieChartData = formatEntityComparison(distributionData, entities)

  const { data: sentimentData, loading: sentimentLoading } = useFetchSentiment(audienceId, entities)
  const barStackedData = formatSentiment(sentimentData, entities)

  return (
    <Vertical
      title={title}
      description={description}
      page={props.index + 1}
      content={() => (
        <Grid
          direction="horizontal"
          gap={48}
          items={[
            {
              component: (
                <Card title={section_1.title} description={section_1.subtitle} showBorder={false}>
                  <Choose>
                    <When condition={distributionLoading}>
                      <LoadingState />
                    </When>
                    <Otherwise>
                      <ZeroState condition={!!pieChartData?.length}>
                        <PieChartHOC data={pieChartData} legendPosition="right-small" />
                      </ZeroState>
                    </Otherwise>
                  </Choose>
                </Card>
              ),
              size: 0.5,
            },
            {
              component: (
                <Card title={section_2.title} description={section_2.subtitle} showBorder={false}>
                  <Choose>
                    <When condition={sentimentLoading}>
                      <LoadingState />
                    </When>
                    <Otherwise>
                      <ZeroState condition={!!barStackedData?.length}>
                        <BarStackedHOC data={barStackedData} />
                      </ZeroState>
                    </Otherwise>
                  </Choose>
                </Card>
              ),
              size: 0.5,
            },
          ]}
        />
      )}
    />
  )
}

export default CompetitiveLandscape
